import type { ExpertListingProfileRequest } from "@shopexperts/shared";
import type { DetailsFormSchema } from "../../schemas/detailsForm";

/**
 * Transforms profile form data to API-ready format
 * Pure function - no side effects, no async operations
 * Consistently uses media_id for both agency and freelancer profiles
 */
export function transformProfileToApi(
    detailsModel: DetailsFormSchema | undefined,
    slug: string,
    hasActiveProSubscription = true,
): ExpertListingProfileRequest | undefined {
    if (!detailsModel) return undefined;

    const model = detailsModel;

    // Extract media_id from avatar upload item (consistent for both types)
    const avatarMediaId = model.avatar?.media_id;

    // Base fields common to both types
    const baseProfile: Partial<ExpertListingProfileRequest> = {
        slug: slug || '', // Will be generated by backend if not provided
        address: {
            address: model.businessAddress || "",
        },
        bio: model.shortBio || "",
        country: model.country || "",
        city_id: model.cityId ? Number(model.cityId) : null,
        starting_price: model.startingPrice || 0,
        timezone: model.timezone || "",
        phone_no: model.phone || "",
        languages: model.languages || [],
        subcategories: (model.subcategories || []).map((id) => ({ id: Number(id) })),
        profile_picture_media_id: avatarMediaId,
        badge_ids: model.badgeIds || [],
        social_links: {
            website: model.website || "",
            linkedin_url: model.linkedin || "",
            github_url: model.github || "",
            x_url: model.x || "",
            fiverr_url: model.fiverr || "",
            upwork_url: model.upwork || "",
            toptal_url: model.toptal || "",
            storetasker_url: model.storetasker || "",
            other_listing_url: model.otherListing || "",
            shopify_directory_link: model.shopifyDirectoryLink || "",
            booking_call_url: model.bookingCallUrl || "",
            google_merchant: model.googleMerchant || "",
        },
    };

    if (hasActiveProSubscription) {
        baseProfile.geo_regions = model.geoRegions || [];
    }

    if (model.expertType === 'agency') {
        const representativePictureMediaId = model.representativePicture?.media_id;
        const isAppServiceProvider = model.agencyType === 'app_service_provider';

        return {
            ...baseProfile,
            expert_type: 'agency',
            agency_name: model.agencyName || "",
            representative_picture_media_id: representativePictureMediaId,
            representative_name: model.representativeName || '',
            representative_role: model.representativeRole || '',
            agency_type: model.agencyType ?? null,
            agency_type_fields: isAppServiceProvider
                ? {
                    ...(model.agencyTypeFields || {}),
                    app_tier: model.appTier ? 'built_for_shopify' : null,
                }
                : model.agencyTypeFields,
            partner_tier: isAppServiceProvider ? null : (model.partnerTier || null),
        } as ExpertListingProfileRequest;
    } else {
        return {
            ...baseProfile,
            expert_type: 'freelancer',
            first_name: model.firstName || "",
            last_name: model.lastName || "",
            role: model.role || "",
            partner_tier: model.partnerTier || null,
        } as ExpertListingProfileRequest;
    }
}
